import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Card, CardContent, Alert, Dialog,
    DialogTitle, DialogContent, DialogActions, LinearProgress
} from '@mui/material';
import axios from 'axios';
import { url } from '../../helper/url_helper';
import ExamQuestions from './ExamQuestions';
import { toast } from 'react-toastify';

const ExamAttempt = ({ exam, attempt, proctoringStatus, setProctoringStatus, videoTrack, agoraError }) => {
    const navigate = useNavigate();
    const [timeLeft, setTimeLeft] = useState(exam.durationMinutes * 60);
    const [answers, setAnswers] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [pendingWarnings, setPendingWarnings] = useState(0);
    const videoRef = useRef(null);

    // Enter fullscreen mode on component mount
    useEffect(() => {
        const enterFullscreen = async () => {
            try {
                if (!document.fullscreenElement) {
                    await document.documentElement.requestFullscreen();
                }
            } catch (error) {
                console.error('Error entering fullscreen:', error);
            }
        };

        enterFullscreen();

        return () => {
            if (document.fullscreenElement) {
                document.exitFullscreen().catch(err => console.error('Error exiting fullscreen:', err));
            }
        };
    }, []);

    // Setup video playback
    useEffect(() => {
        const playVideo = async () => {
            if (videoRef.current && videoTrack) {
                try {
                    await videoTrack.play(videoRef.current);
                } catch (error) {
                    console.error('Error playing video:', error);
                }
            }
        };

        playVideo();

        return () => {
            if (videoTrack) {
                videoTrack.stop();
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
        };
    }, [videoTrack]);

    // Timer countdown
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleSubmitExam();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    // Handle visibility changes, tab/window switches, and fullscreen changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                const newPendingWarnings = pendingWarnings + 1;
                setPendingWarnings(newPendingWarnings);

                setWarningMessage(`Warning: Do not switch tabs or windows during the exam! 
                    You have ${exam.maxWarnings - proctoringStatus.warnings} warnings remaining.`);
                setShowWarningModal(true);

                // If this is the 3rd pending warning, actually add it to the count
                if (newPendingWarnings >= 3) {
                    const newWarnings = proctoringStatus.warnings + 1;
                    setProctoringStatus(prev => ({
                        ...prev,
                        warnings: newWarnings
                    }));
                    setPendingWarnings(0);

                    // If max warnings reached, submit exam
                    if (newWarnings >= exam.maxWarnings) {
                        handleSubmitExam();
                    }
                }
            }
        };

        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                const newPendingWarnings = pendingWarnings + 1;
                setPendingWarnings(newPendingWarnings);

                setWarningMessage(`Warning: Exam must be taken in fullscreen mode! 
                    You have ${exam.maxWarnings - proctoringStatus.warnings} warnings remaining.`);
                setShowWarningModal(true);

                // Try to re-enter fullscreen
                document.documentElement.requestFullscreen().catch(err => {
                    console.error('Error re-entering fullscreen:', err);
                });

                // If this is the 3rd pending warning, actually add it to the count
                if (newPendingWarnings >= 3) {
                    const newWarnings = proctoringStatus.warnings + 1;
                    setProctoringStatus(prev => ({
                        ...prev,
                        warnings: newWarnings
                    }));
                    setPendingWarnings(0);

                    // If max warnings reached, submit exam
                    if (newWarnings >= exam.maxWarnings) {
                        handleSubmitExam();
                    }
                }
            }
        };

        // Prevent context menu (right-click)
        const handleContextMenu = (e) => {
            e.preventDefault();
        };

        // Prevent keyboard shortcuts
        const handleKeyDown = (e) => {
            // Block F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C, Ctrl+U
            if (
                e.key === 'F12' ||
                (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
                (e.ctrlKey && e.key === 'u')
            ) {
                e.preventDefault();
                const newPendingWarnings = pendingWarnings + 1;
                setPendingWarnings(newPendingWarnings);

                setWarningMessage(`Warning: Developer tools are not allowed during the exam! 
                    You have ${exam.maxWarnings - proctoringStatus.warnings} warnings remaining.`);
                setShowWarningModal(true);

                if (newPendingWarnings >= 3) {
                    const newWarnings = proctoringStatus.warnings + 1;
                    setProctoringStatus(prev => ({
                        ...prev,
                        warnings: newWarnings
                    }));
                    setPendingWarnings(0);

                    if (newWarnings >= exam.maxWarnings) {
                        handleSubmitExam();
                    }
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [proctoringStatus.warnings, pendingWarnings]);

    const handleSubmitExam = async () => {
        try {
            const studentId = JSON.parse(localStorage.getItem('student'))?.id;

            if (!studentId) {
                toast.error('Student ID not found. Please login again.');
                return;
            }

            // Prepare request data matching your SubmitExamRequest DTO
            const requestData = {
                answers: answers, // Your answers map {questionId: selectedAnswer}
                warnings: proctoringStatus.warnings
            };

            const response = await axios.post(
                `${url}submitExam/${attempt?.id}`, // Using attempt.id from your state
                requestData,
                {
                    headers: {
                        'X-User-ID': studentId,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Check response success flag
            if (response.data?.success) {
                toast.success(response.data.message || 'Exam submitted successfully!');
                navigate(`/myExams`);
            } else if (response.data?.message === "Exam time has expired") {
                toast.error(response.data?.message || 'Exam submission failed');
                navigate(`/myExams`);
            } else {
                toast.error(response.data?.message || 'Exam submission failed');
                navigate(`/myExams`);
            }

        } catch (error) {
            console.error('Error submitting exam:', error);

            // Show detailed error message if available
            const errorMessage = error.response?.data?.message
                || error.message
                || 'Failed to submit exam. Please try again.';

            toast.error(errorMessage);

            // Optional: Handle specific error cases
            if (error.response?.status === 401) {
                // Handle unauthorized (e.g., redirect to login)
            }
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex < exam.questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handlePreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleCloseWarningModal = async () => {
        setShowWarningModal(false);
        if (!document.fullscreenElement) {
            await document.documentElement.requestFullscreen();
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                p: 2,
                bgcolor: '#f5f5f5'
            }}>
                <Typography variant="h6">{exam.title}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Typography>Time Left: {formatTime(timeLeft)}</Typography>
                    <Typography color="error">
                        Warnings: {proctoringStatus.warnings}/{exam.maxWarnings}
                    </Typography>
                </Box>
            </Box>

            {/* Main Content */}
            <Box sx={{ display: 'flex', gap: 3 }}>
                {/* Questions Section */}
                <Box sx={{ flex: 3 }}>
                    <ExamQuestions
                        question={exam.questions[currentQuestionIndex]}
                        questionNumber={currentQuestionIndex + 1}
                        totalQuestions={exam.questions.length}
                        answers={answers}
                        setAnswers={setAnswers}
                    />

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            variant="contained"
                            disabled={currentQuestionIndex === 0}
                            onClick={handlePreviousQuestion}
                        >
                            Previous
                        </Button>

                        {currentQuestionIndex < exam.questions.length - 1 ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNextQuestion}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowSubmitConfirm(true)}
                            >
                                Submit Exam
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Proctoring Panel */}
                <Box sx={{ flex: 1 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Proctoring Status
                            </Typography>

                            <LinearProgress
                                variant="determinate"
                                value={(proctoringStatus.warnings / exam.maxWarnings) * 100}
                                color={proctoringStatus.warnings >= exam.maxWarnings ? 'error' : 'warning'}
                                sx={{ height: 8, mb: 3 }}
                            />

                            <Typography variant="body2" gutterBottom>
                                Camera: <span style={{ color: proctoringStatus.camera ? 'green' : 'red' }}>
                                    ● {proctoringStatus.camera ? 'Active' : 'Inactive'}
                                </span>
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                Microphone: <span style={{ color: proctoringStatus.microphone ? 'green' : 'red' }}>
                                    ● {proctoringStatus.microphone ? 'Active' : 'Inactive'}
                                </span>
                            </Typography>
                            <Typography variant="body2">
                                Screen Share: <span style={{ color: proctoringStatus.screen ? 'green' : 'red' }}>
                                    ● {proctoringStatus.screen ? 'Active' : 'Inactive'}
                                </span>
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Question Navigation */}
                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Questions
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {exam.questions.map((_, index) => (
                                    <Button
                                        key={index}
                                        variant={currentQuestionIndex === index ? "contained" : "outlined"}
                                        color={answers[exam.questions[index].id] ? "success" : "primary"}
                                        onClick={() => setCurrentQuestionIndex(index)}
                                        sx={{ minWidth: 40, height: 40 }}
                                    >
                                        {index + 1}
                                    </Button>
                                ))}
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    Answered: {Object.keys(answers).length}/{exam.questions.length}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Camera Preview Card */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Your Camera
                            </Typography>
                            {agoraError ? (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {agoraError}
                                </Alert>
                            ) : (
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    style={{
                                        width: '100%',
                                        height: 200,
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        transform: 'scaleX(-1)'
                                    }}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Submit Confirmation Dialog */}
            <Dialog open={showSubmitConfirm} onClose={() => setShowSubmitConfirm(false)}>
                <DialogTitle>Confirm Submission</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to submit your exam? You have answered {Object.keys(answers).length} out of {exam.questions.length} questions.
                    </Typography>
                    {Object.keys(answers).length < exam.questions.length && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            You have {exam.questions.length - Object.keys(answers).length} unanswered questions.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSubmitConfirm(false)}>Cancel</Button>
                    <Button
                        onClick={handleSubmitExam}
                        variant="contained"
                        color="primary"
                    >
                        Submit Exam
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Warning Modal */}
            <Dialog open={showWarningModal} onClose={handleCloseWarningModal}>
                <DialogTitle color="warning.main">Proctoring Warning</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {warningMessage}
                    </Alert>
                    <Typography variant="body2">
                        Repeated violations may result in automatic exam submission.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseWarningModal}
                        color="warning"
                        variant="contained"
                    >
                        I Understand
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExamAttempt;
